// src/net/ghostClient.ts — CLIENT API for GHOST DUELS (Slice B) raceable ghosts. Pure `fetch`; no
// Anthropic SDK; NO key. Mirrors report.ts's write-degradation + leaderboard.ts's read-degradation
// doctrine: EVERY failure (network / non-2xx / timeout / malformed body) degrades to null / an empty
// list — never throws. A cold, missing, or bv-mismatched ghost store must leave the run PLAYING with
// static footprints or no ghost (GHOST_DUELS_BRIEF §3), so this layer never surfaces an error.
//
//   putGhost(record)        -> { id } | null   (opt-in upload at the daily score submit; fire-and-forget)
//   getGhost(id)            -> GhostRecord | null   (a `&gr=<id>` deep link resolves the actual replay)
//   getDailyGhosts(date, n) -> GhostRecord[]   (the day's best ghosts for the ladder; [] on any failure)

import type { GhostRecord } from './types';

const DEFAULT_TIMEOUT_MS = 4_000;

function apiBase(): string {
  const raw = import.meta.env?.VITE_API_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

/** Fetch with a hard timeout; any throw/timeout bubbles to the caller's try/catch, which degrades. */
async function withTimeout(fn: (signal: AbortSignal) => Promise<Response>, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fn(ctrl.signal);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Coerce an unknown server body into a safe GhostRecord, or null. The server already validates on write,
 * but the reader stays defensive: a shape that isn't a v1 ghost with a non-empty track degrades to null so
 * a malformed/foreign response can never reach the replay decoder.
 */
function normalizeRecord(raw: unknown): GhostRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1) return null;
  const bv = typeof o.bv === 'string' ? o.bv : '';
  const day = typeof o.day === 'string' ? o.day : '';
  const topic = typeof o.topic === 'string' ? o.topic : '';
  const name = typeof o.name === 'string' ? o.name : 'Anonymous';
  const track = typeof o.track === 'string' ? o.track : '';
  const seed = typeof o.seed === 'number' && Number.isFinite(o.seed) ? o.seed : NaN;
  const m = typeof o.m === 'number' && Number.isFinite(o.m) ? o.m : NaN;
  const score = typeof o.score === 'number' && Number.isFinite(o.score) ? o.score : 0;
  const durationMs = typeof o.durationMs === 'number' && Number.isFinite(o.durationMs) ? o.durationMs : 0;
  if (!bv || !topic || !track || Number.isNaN(seed) || Number.isNaN(m)) return null;
  return { v: 1, bv, day, topic, seed, m, name, score, durationMs, track };
}

/**
 * Upload a finished daily run as a public raceable ghost (opt-in). Returns the new id on success, else
 * null on ANY failure — the caller treats a null as "no shareable ghost minted" and shares the static
 * footprint fallback exactly as today.
 */
export async function putGhost(record: GhostRecord): Promise<{ id: string } | null> {
  try {
    const res = await withTimeout((signal) =>
      fetch(`${apiBase()}/ghosts`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(record),
        signal,
      }),
    );
    if (!res.ok) return null;
    const body = (await res.json()) as { accepted?: boolean; id?: unknown };
    if (body?.accepted !== true || typeof body.id !== 'string' || body.id.length === 0) return null;
    return { id: body.id };
  } catch {
    return null;
  }
}

/** Fetch a stored ghost by id, or null on any failure (absent / 404 / network / timeout / malformed). */
export async function getGhost(id: string): Promise<GhostRecord | null> {
  const clean = id.trim();
  if (clean === '' || !/^[A-Za-z0-9-]{1,64}$/.test(clean)) return null;
  try {
    const res = await withTimeout((signal) =>
      fetch(`${apiBase()}/ghosts/${encodeURIComponent(clean)}`, { method: 'GET', headers: { accept: 'application/json' }, signal }),
    );
    if (!res.ok) return null;
    return normalizeRecord(await res.json());
  } catch {
    return null;
  }
}

/** The day's top-N ghosts (descending). Empty on any failure or a cold day — never throws. */
export async function getDailyGhosts(date: string, n: number): Promise<GhostRecord[]> {
  try {
    const params = new URLSearchParams();
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) params.set('date', date);
    params.set('n', String(Math.min(10, Math.max(1, Math.floor(n) || 1))));
    const res = await withTimeout((signal) =>
      fetch(`${apiBase()}/ghosts/daily?${params.toString()}`, { method: 'GET', headers: { accept: 'application/json' }, signal }),
    );
    if (!res.ok) return [];
    const body = (await res.json()) as { ghosts?: unknown };
    if (!Array.isArray(body?.ghosts)) return [];
    const out: GhostRecord[] = [];
    for (const raw of body.ghosts) {
      const rec = normalizeRecord(raw);
      if (rec) out.push(rec);
    }
    return out;
  } catch {
    return [];
  }
}
